import { Rating } from '@/components/ui/rating-distribution';
import { UnitSystem } from '@/hooks/use-units-preference';
import { useAppTheme } from '@/providers/theme-provider';
import { calculateDistance } from '@/utils/distance';
import { imageUrl } from '@/utils/image-url';
import Ionicons from '@expo/vector-icons/Ionicons';
import React from 'react';
import { ActivityIndicator, Image, Pressable, ScrollView, Text, View } from 'react-native';

interface SearchTypeaheadProps {
  showTypeahead: boolean;
  searchBarBottom: number;
  dropdownTop: number;
  isLoading: boolean;
  hasResults: boolean;
  hasBusinessResults: boolean;
  hasUserResults: boolean;
  hasMenuItemResults: boolean;
  mappedResults: { items: any[] } | null;
  mappedUserResults: { items: any[] } | null;
  menuItemSearchResults: { items: any[] } | undefined;
  coords: { latitude: number; longitude: number } | null;
  units: UnitSystem;
  onBackdropPress: () => void;
  onBusinessPress: (id: string) => void;
  onUserPress: (identifier: string) => void;
  onMenuItemPress: (id: string) => void;
  onMoreResultsPress: () => void;
}

export function SearchTypeahead({
  showTypeahead,
  searchBarBottom,
  dropdownTop,
  isLoading,
  hasResults,
  hasBusinessResults,
  hasUserResults,
  hasMenuItemResults,
  mappedResults,
  mappedUserResults,
  menuItemSearchResults,
  coords,
  units,
  onBackdropPress,
  onBusinessPress,
  onUserPress,
  onMenuItemPress,
  onMoreResultsPress,
}: SearchTypeaheadProps) {
  const theme = useAppTheme();

  if (!showTypeahead) return null;

  return (
    <>
      {searchBarBottom > 0 && (
        <Pressable
          onPress={onBackdropPress}
          style={{
            position: 'absolute',
            top: searchBarBottom,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            zIndex: 999,
            elevation: 999,
          }}
        />
      )}

      <View
        className="bg-surface border border-border"
        style={{
          position: 'absolute',
          top: dropdownTop,
          left: 16,
          right: 16,
          borderRadius: 8,
          maxHeight: 300,
          zIndex: 1000,
          elevation: 1000,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.25,
          shadowRadius: 12,
          overflow: 'hidden',
        }}
      >
        {isLoading ? (
          <View className="p-4 items-center">
            <ActivityIndicator size="small" color={theme.colors.primary} />
          </View>
        ) : hasResults ? (
          <ScrollView
            nestedScrollEnabled
            showsVerticalScrollIndicator={true}
            style={{ maxHeight: 300 }}
            contentContainerStyle={{ paddingBottom: 0 }}
          >
            {hasUserResults && mappedUserResults && (
              <>
                <View className="px-3 pt-3 pb-2">
                  <Text className="text-xs font-semibold uppercase text-muted-foreground" accessibilityRole="header">
                    People
                  </Text>
                </View>
                {mappedUserResults.items.map((user) => {
                  const username = user.username || user.displayUsername || '';
                  const normalizedUsername = username.replace(/^@+/, '');
                  const displayUsername = normalizedUsername ? `@${normalizedUsername}` : '';
                  const userAvatar = imageUrl(user.image || user.avatar, 'thumbnail');

                  return (
                    <Pressable
                      key={user.id}
                      onPress={() => onUserPress(user.username?.replace(/^@+/, '') || user.id)}
                      className="p-3 flex-row items-center border-b border-border"
                    >
                      {userAvatar ? (
                        <Image
                          source={{ uri: userAvatar }}
                          className="w-10 h-10 rounded-full mr-3 bg-surface-secondary"
                          accessibilityLabel={`${user.name || 'Unknown User'}'s profile photo`}
                        />
                      ) : (
                        <View className="w-10 h-10 rounded-full mr-3 items-center justify-center bg-surface-secondary">
                          <Ionicons name="person" size={20} className="text-muted-foreground" />
                        </View>
                      )}

                      <View className="flex-1">
                        <Text className="font-semibold mb-0.5 text-foreground">
                          {user.name || 'Unknown User'}
                        </Text>
                        {displayUsername && (
                          <Text className="text-sm text-muted-foreground">
                            {displayUsername}
                          </Text>
                        )}
                      </View>
                    </Pressable>
                  );
                })}
              </>
            )}

            {hasBusinessResults && mappedResults && (
              <>
                <View className={`px-3 pt-3 pb-2 ${hasUserResults ? 'border-t border-border' : ''}`}>
                  <Text className="text-xs font-semibold uppercase text-muted-foreground" accessibilityRole="header">
                    Places
                  </Text>
                </View>
                {mappedResults.items.map((business) => (
                  <Pressable
                    key={business.id}
                    onPress={() => onBusinessPress(business.id)}
                    className="p-3 border-b border-border"
                  >
                    <View className="flex-row items-center justify-between">
                      <View className="flex-1 mr-3">
                        <Text className="font-semibold mb-1 text-foreground">
                          {business.name}
                        </Text>
                        <View className="flex-row items-center">
                          {coords && business.latitude != null && business.longitude != null && (
                            <Text className="text-sm mr-2 text-muted-foreground">
                              {calculateDistance(coords.latitude, coords.longitude, business.latitude, business.longitude, units)}
                            </Text>
                          )}
                          {(business.city || business.state) && (
                            <Text className="text-sm text-muted-foreground">
                              {[business.city, business.state].filter(Boolean).join(', ')}
                            </Text>
                          )}
                        </View>
                      </View>
                      <View className="items-end">
                        <Rating
                          distribution={business.ratingDistribution}
                          size="sm"
                        />
                      </View>
                    </View>
                  </Pressable>
                ))}
              </>
            )}

            {hasMenuItemResults && menuItemSearchResults && (
              <>
                <View className={`px-3 pt-3 pb-2 ${hasUserResults || hasBusinessResults ? 'border-t border-border' : ''}`}>
                  <Text className="text-xs font-semibold uppercase text-muted-foreground" accessibilityRole="header">
                    Menu Items
                  </Text>
                </View>
                {menuItemSearchResults.items.map((menuItem) => (
                  <Pressable
                    key={menuItem.id}
                    onPress={() => onMenuItemPress(menuItem.id)}
                    className="p-3 border-b border-border flex-row items-center"
                  >
                    {menuItem.coverPhotoUrl ? (
                      <Image
                        source={{ uri: imageUrl(menuItem.coverPhotoUrl, 'thumbnail') }}
                        className="w-10 h-10 rounded-lg mr-3 bg-surface-secondary"
                        accessibilityLabel={`Photo of ${menuItem.name}`}
                      />
                    ) : (
                      <View className="w-10 h-10 rounded-lg mr-3 items-center justify-center bg-surface-secondary">
                        <Ionicons name="restaurant" size={20} className="text-muted-foreground" />
                      </View>
                    )}

                    <View className="flex-1">
                      <Text className="font-semibold mb-0.5 text-foreground">
                        {menuItem.name}
                      </Text>
                      {menuItem.businessName && (
                        <Text className="text-sm text-muted-foreground">
                          {menuItem.businessName}
                        </Text>
                      )}
                    </View>
                    <View className="items-end">
                      <Rating
                        distribution={menuItem.ratingDistribution}
                        size="sm"
                      />
                    </View>
                  </Pressable>
                ))}
              </>
            )}

            <Pressable
              onPress={onMoreResultsPress}
              className="p-3 flex-row items-center justify-center border-t border-border"
            >
              <Text className="font-semibold text-primary">
                More results
              </Text>
              <Ionicons name="chevron-forward" size={16} className="text-primary" style={{ marginLeft: 4 }} />
            </Pressable>
          </ScrollView>
        ) : (
          <View className="p-4">
            <Text className="text-center text-muted-foreground">
              No matches
            </Text>
          </View>
        )}
      </View>
    </>
  );
}
